/**
 * ETL Pipeline Executor — walks the DAG in topological order and executes each node.
 *
 * Follows the same architectural pattern as services/workflowExecutor.js but tailored
 * for ETL data pipelines. Source nodes produce data (SQL text, dataframes); target
 * nodes consume it (execute against a database, write to file/S3).
 *
 * Currently supports:
 *   - etl-source-ddl              → returns SQL text + dialect
 *   - etl-source-dml              → returns SQL text + dialect
 *   - etl-transform-sql-dedup     → removes duplicate columns from CREATE TABLE
 *   - etl-transform-sql-types     → fixes VARCHAR(255) using PIC comments
 *   - etl-transform-sql-dialect   → converts between SQL dialects
 *   - etl-target-sql-executor     → executes SQL against connected database via ETL server
 */

import { executeSql } from './etlFileService'

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

export const EtlEvents = {
  PIPELINE_START: 'pipeline_start',
  PIPELINE_COMPLETE: 'pipeline_complete',
  PIPELINE_ERROR: 'pipeline_error',
  NODE_START: 'node_start',
  NODE_PROGRESS: 'node_progress',
  NODE_COMPLETE: 'node_complete',
  NODE_ERROR: 'node_error',
  NODE_SKIPPED: 'node_skipped',
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Collect SQL text from all upstream node results.
 */
function collectUpstreamSql(upstreamData) {
  const allSql = []
  let dialect = 'db2'
  let create_database = false
  let database_name = ''
  for (const data of upstreamData) {
    if (data?.sql) {
      allSql.push(data.sql)
      if (data.dialect) dialect = data.dialect
      if (data.create_database) create_database = true
      if (data.database_name) database_name = data.database_name
    }
  }
  return { combinedSql: allSql.join('\n\n'), dialect, create_database, database_name }
}

/**
 * Map a driver ID from the Connection Manager to the backend driver key.
 */
function resolveDriverKey(driverId) {
  const map = {
    postgresql: 'postgresql',
    postgres: 'postgresql',
    mysql: 'mysql',
    sqlserver: 'sqlserver',
    mssql: 'sqlserver',
    oracle: 'oracle',
    db2: 'db2',
    'ibm-db2': 'db2',
    ibmdb: 'db2',
  }
  const key = (driverId || '').toLowerCase()
  if (map[key]) return map[key]
  // Handle nodeType-style IDs like "etl-source-db2" → strip prefix, try again
  const suffix = key.replace(/^etl-(?:source|target)-/, '')
  if (map[suffix]) return map[suffix]
  return suffix || driverId || ''
}

// ---------------------------------------------------------------------------
// Source node executors
// ---------------------------------------------------------------------------

/**
 * Execute a DDL or DML source node.
 * These are pure pass-through — they just return the SQL text the user entered.
 */
function executeSourceNode(node) {
  const config = node.data?.config || {}
  const sql = config.sql_script || ''
  const dialect = config.dialect || 'db2'

  if (!sql.trim()) {
    throw new Error(`${node.data?.label || 'Source'}: No SQL script provided. Paste SQL or upload a file.`)
  }

  // Count statements for summary
  const stmtCount = sql.split(/;\s*$/m).filter(s => s.trim() && !s.trim().startsWith('--')).length

  return {
    type: 'sql',
    sql,
    dialect,
    statementCount: stmtCount,
    label: node.data?.label || 'SQL Source',
    // Pass through execution config so Database Target can pick it up
    create_database: config.create_database || false,
    database_name: config.database_name || '',
  }
}

// ---------------------------------------------------------------------------
// SQL Transform executors
// ---------------------------------------------------------------------------

/**
 * SQL Deduplicator — removes duplicate column definitions from CREATE TABLE statements.
 */
function executeSqlDedup(node, upstreamData, onProgress) {
  const config = node.data?.config || {}
  const keepStrategy = config.keep_strategy || 'first'
  const logRemovals = config.log_removals !== false
  const dedupPk = config.dedup_pk !== false

  const { combinedSql, dialect, create_database, database_name } = collectUpstreamSql(upstreamData)
  if (!combinedSql.trim()) {
    throw new Error('SQL Deduplicator: No SQL received from upstream.')
  }

  onProgress(0, 'Scanning for duplicate columns...')

  let totalRemoved = 0
  let tablesProcessed = 0
  let tablesDropped = 0
  const changes = []
  const seenTables = new Set() // track table names to drop entire duplicate CREATE TABLE blocks

  // Process each CREATE TABLE block
  const result = combinedSql.replace(
    /CREATE\s+TABLE\s+([^\s(]+)\s*\(([\s\S]*?)\)\s*;/gi,
    (match, tableName, body) => {
      // --- Table-level dedup: if we already saw this table name, drop the entire block ---
      const tableKey = tableName.replace(/["'`\[\]]/g, '').toLowerCase()
      if (seenTables.has(tableKey)) {
        tablesDropped++
        return '' // remove entire duplicate CREATE TABLE
      }
      seenTables.add(tableKey)

      tablesProcessed++
      const lines = body.split('\n')
      const seen = new Map() // lowercase col name → line index
      const keepLines = []
      let removedInTable = 0

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const trimmed = line.trim()

        // Skip empty lines and comments
        if (!trimmed || trimmed.startsWith('--')) {
          keepLines.push(line)
          continue
        }

        // Check if this is a CONSTRAINT line (PK, FK, etc.)
        if (/^\s*CONSTRAINT\s/i.test(trimmed)) {
          if (dedupPk) {
            // Dedup PK column list
            const pkMatch = trimmed.match(/PRIMARY\s+KEY\s*\(([^)]+)\)/i)
            if (pkMatch) {
              const pkCols = pkMatch[1].split(',').map(c => c.trim())
              const uniquePkCols = []
              const seenPk = new Set()
              for (const col of pkCols) {
                const colLower = col.toLowerCase()
                if (!seenPk.has(colLower)) {
                  seenPk.add(colLower)
                  uniquePkCols.push(col)
                }
              }
              if (uniquePkCols.length < pkCols.length) {
                const dedupedPk = trimmed.replace(
                  /PRIMARY\s+KEY\s*\([^)]+\)/i,
                  `PRIMARY KEY (${uniquePkCols.join(', ')})`
                )
                keepLines.push(line.replace(trimmed, dedupedPk))
                continue
              }
            }
          }
          keepLines.push(line)
          continue
        }

        // Extract column name — handles "col_name" or [col_name] or `col_name` or bare col_name
        const colMatch = trimmed.match(/^["'`\[]?(\w+)["'`\]]?\s/i)
        if (!colMatch) {
          keepLines.push(line)
          continue
        }

        const colName = colMatch[1].toLowerCase()

        if (seen.has(colName)) {
          // Duplicate found
          if (keepStrategy === 'last') {
            // Replace the previous occurrence with this one
            const prevIdx = seen.get(colName)
            keepLines[prevIdx] = null // mark for removal
            keepLines.push(line)
            seen.set(colName, keepLines.length - 1)
          }
          // For 'first' strategy, just skip this line
          removedInTable++
          totalRemoved++
        } else {
          seen.set(colName, keepLines.length)
          keepLines.push(line)
        }
      }

      if (removedInTable > 0) {
        const cleanName = tableName.replace(/["'`\[\]]/g, '')
        const originalCount = seen.size + removedInTable
        changes.push(`${cleanName}: ${originalCount} → ${seen.size} columns (removed ${removedInTable} duplicates)`)
        if (logRemovals) {
          onProgress(Math.round((tablesProcessed / 40) * 80), `${cleanName}: removed ${removedInTable} duplicate columns`)
        }
      }

      // Rebuild: filter nulls, fix trailing commas
      const finalLines = keepLines.filter(l => l !== null)

      // Fix trailing commas — remove comma from last column line before CONSTRAINT or closing paren
      const cleanedBody = fixTrailingCommas(finalLines.join('\n'))

      return `CREATE TABLE ${tableName} (\n${cleanedBody}\n);`
    }
  )

  if (tablesDropped > 0) {
    changes.push(`Dropped ${tablesDropped} duplicate CREATE TABLE blocks (same table name appeared multiple times)`)
  }

  onProgress(100, `Deduplicated ${tablesProcessed} tables: removed ${totalRemoved} duplicate columns, ${tablesDropped} duplicate table blocks`)

  return {
    type: 'sql',
    sql: result,
    dialect,
    label: node.data?.label || 'SQL Deduplicator',
    changes,
    totalRemoved,
    tablesProcessed,
    tablesDropped,
    create_database,
    database_name,
  }
}

/**
 * Fix comma placement in CREATE TABLE column definitions.
 *
 * The sql_generator.py output places commas AFTER inline `-- PIC` comments,
 * which means the commas are inside the SQL line comment and invisible to the
 * database engine.  This function:
 *   1. Strips ALL trailing commas (whether outside, before, or inside comments).
 *   2. Re-adds commas to every content line except the last one (CONSTRAINT or
 *      final column), placing them before any `--` comment.
 */
function fixTrailingCommas(body) {
  const lines = body.split('\n')

  // Identify content-line indices (non-empty, non-pure-comment)
  const contentIndices = []
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim()
    if (trimmed && !trimmed.startsWith('--')) {
      contentIndices.push(i)
    }
  }
  if (contentIndices.length === 0) return body

  for (let k = 0; k < contentIndices.length; k++) {
    const idx = contentIndices[k]
    let line = lines[idx]

    // --- Strip existing commas ---
    // Case 1: comma at very end of line (possibly inside a comment)
    //         e.g.  "COL" INT NOT NULL  -- PIC 9(9),
    line = line.replace(/,(\s*)$/, '$1')
    // Case 2: comma immediately before a comment
    //         e.g.  "COL" INT NOT NULL, -- comment
    line = line.replace(/,(\s+--.*)$/, '$1')

    // --- Re-add comma to every line except the last content line ---
    if (k < contentIndices.length - 1) {
      const commentPos = line.indexOf('  --')
      if (commentPos > 0) {
        const sqlPart = line.substring(0, commentPos).trimEnd()
        const commentPart = line.substring(commentPos)
        line = sqlPart + ',' + commentPart
      } else {
        line = line.trimEnd() + ','
      }
    }

    lines[idx] = line
  }

  return lines.join('\n')
}

/**
 * Type Normalizer — fixes VARCHAR(255) using inline PIC comments.
 */
function executeSqlTypeNormalizer(node, upstreamData, onProgress) {
  const config = node.data?.config || {}
  const fixVarchar255 = config.fix_varchar255 !== false
  const usePicComments = config.use_pic_comments !== false

  const { combinedSql, dialect, create_database, database_name } = collectUpstreamSql(upstreamData)
  if (!combinedSql.trim()) {
    throw new Error('Type Normalizer: No SQL received from upstream.')
  }

  onProgress(0, 'Scanning for type corrections...')

  let totalFixed = 0
  const changes = []

  let result = combinedSql

  if (fixVarchar255 && usePicComments) {
    // Fix VARCHAR(255) where a PIC comment reveals the actual length
    result = result.replace(
      /VARCHAR\(255\)(\s+(?:NOT\s+NULL|NULL)?)(\s*--\s*PIC\s+(.+?))\s*$/gm,
      (match, nullability, commentPart, picClause) => {
        const length = parsePicLength(picClause.trim())
        if (length && length !== 255) {
          totalFixed++
          return `VARCHAR(${length})${nullability}${commentPart}`
        }
        return match
      }
    )
  }

  onProgress(100, `Fixed ${totalFixed} VARCHAR(255) → actual PIC lengths`)
  if (totalFixed > 0) {
    changes.push(`Fixed ${totalFixed} VARCHAR(255) columns to actual PIC lengths`)
  }

  return {
    type: 'sql',
    sql: result,
    dialect,
    label: node.data?.label || 'Type Normalizer',
    changes,
    create_database,
    database_name,
    totalFixed,
  }
}

/**
 * Parse a COBOL PIC clause to determine the character length.
 * Handles: PIC X(4) → 4, PIC XX → 2, PIC XXXX → 4, PIC X(15) → 15,
 *          PIC A(8) → 8, PIC AA → 2, PIC X(08) → 8
 */
function parsePicLength(pic) {
  if (!pic) return null
  const upper = pic.toUpperCase().trim()

  // PIC X(n) or PIC A(n) — parenthesized form
  const parenMatch = upper.match(/^[XA]\((\d+)\)$/)
  if (parenMatch) return parseInt(parenMatch[1], 10)

  // PIC XX, PIC XXXX, PIC AA, etc. — consecutive chars
  const repeatMatch = upper.match(/^([XA]+)$/)
  if (repeatMatch) return repeatMatch[1].length

  // Mixed forms like PIC X(4)X — just use the parenthesized part
  const mixedMatch = upper.match(/[XA]\((\d+)\)/)
  if (mixedMatch) return parseInt(mixedMatch[1], 10)

  return null
}

/**
 * Dialect Converter — converts SQL between database dialects.
 */
function executeSqlDialectConverter(node, upstreamData, onProgress) {
  const config = node.data?.config || {}
  const sourceDialect = config.source_dialect || 'sqlserver'
  const targetDialect = config.target_dialect || 'db2'

  const { combinedSql } = collectUpstreamSql(upstreamData)
  if (!combinedSql.trim()) {
    throw new Error('Dialect Converter: No SQL received from upstream.')
  }

  // --- Strip stored procedures (runs BEFORE same-dialect check) ---
  let result = combinedSql
  const changes = []
  const stripProcedures = config.strip_procedures !== false
  if (stripProcedures) {
    // Match both SQL Server (CREATE OR ALTER) and DB2/PG/Oracle (CREATE OR REPLACE)
    // Also match plain CREATE PROCEDURE (no OR ALTER/REPLACE)
    const procRegex = /(?:--[^\n]*\n)*CREATE\s+(?:OR\s+(?:ALTER|REPLACE)\s+)?PROCEDURE\b[\s\S]*?\nEND\s*;\s*/gi
    const procMatches = result.match(procRegex)
    const strippedCount = procMatches ? procMatches.length : 0
    if (strippedCount > 0) {
      result = result.replace(procRegex, '')
      changes.push(`Stripped ${strippedCount} stored procedures`)
      onProgress(10, `Stripped ${strippedCount} stored procedures`)
    }
  }

  if (sourceDialect === targetDialect) {
    onProgress(100, changes.length > 0
      ? `Same dialect — no conversion needed. ${changes.join('. ')}`
      : 'Source and target dialects are the same — no conversion needed.')
    return {
      type: 'sql',
      sql: result,
      dialect: targetDialect,
      label: node.data?.label || 'Dialect Converter',
      changes: changes.length > 0 ? changes : ['No conversion needed — same dialect'],
    }
  }

  onProgress(0, `Converting from ${sourceDialect} to ${targetDialect}...`)

  let identifierCount = 0
  let typeCount = 0
  let functionCount = 0

  // --- Identifier quoting ---

  if (sourceDialect === 'sqlserver') {
    // SQL Server [brackets] → target quoting
    const targetQuote = targetDialect === 'mysql' ? '`' : '"'
    result = result.replace(/\[([^\]]+)\]/g, (_, name) => {
      identifierCount++
      return `${targetQuote}${name}${targetQuote}`
    })
  } else if (sourceDialect === 'mysql') {
    // MySQL `backticks` → target quoting
    const targetQuote = targetDialect === 'sqlserver' ? ['[', ']'] : ['"', '"']
    result = result.replace(/`([^`]+)`/g, (_, name) => {
      identifierCount++
      return `${targetQuote[0]}${name}${targetQuote[1]}`
    })
  } else if (sourceDialect === 'db2' || sourceDialect === 'postgresql' || sourceDialect === 'oracle') {
    // Double-quoted → target quoting
    if (targetDialect === 'sqlserver') {
      result = result.replace(/"([^"]+)"/g, (_, name) => {
        // Skip if it looks like a string literal (inside VALUES or SET)
        identifierCount++
        return `[${name}]`
      })
    } else if (targetDialect === 'mysql') {
      result = result.replace(/"([^"]+)"/g, (_, name) => {
        identifierCount++
        return `\`${name}\``
      })
    }
    // db2/pg/oracle → db2/pg/oracle: double quotes stay
  }

  if (identifierCount > 0) {
    changes.push(`Converted ${identifierCount} identifier quotes (${sourceDialect} → ${targetDialect})`)
  }

  // --- Type remapping ---

  const typeRemaps = getTypeRemaps(sourceDialect, targetDialect)
  for (const [pattern, replacement] of typeRemaps) {
    const before = result
    result = result.replace(pattern, replacement)
    if (result !== before) typeCount++
  }

  if (typeCount > 0) {
    changes.push(`Applied ${typeCount} type conversions`)
  }

  // --- Function remapping ---

  const funcRemaps = getFunctionRemaps(sourceDialect, targetDialect)
  for (const [pattern, replacement] of funcRemaps) {
    const before = result
    result = result.replace(pattern, replacement)
    if (result !== before) functionCount++
  }

  if (functionCount > 0) {
    changes.push(`Applied ${functionCount} function conversions`)
  }

  // Count CREATE TABLE statements for summary
  const tableCount = (result.match(/CREATE\s+TABLE/gi) || []).length

  onProgress(100, `Converted ${tableCount} tables from ${sourceDialect} → ${targetDialect} (${identifierCount} identifiers, ${typeCount} types, ${functionCount} functions)`)

  return {
    type: 'sql',
    sql: result,
    dialect: targetDialect,
    label: node.data?.label || 'Dialect Converter',
    changes,
  }
}

/**
 * Get type remapping rules for a dialect conversion.
 * Returns array of [regex, replacement] pairs.
 */
function getTypeRemaps(source, target) {
  const remaps = []

  // SQL Server → DB2 / PostgreSQL
  if (source === 'sqlserver' && (target === 'db2' || target === 'postgresql')) {
    remaps.push([/\bNVARCHAR\((\d+)\)/gi, 'VARCHAR($1)'])
    remaps.push([/\bNVARCHAR\(MAX\)/gi, 'CLOB'])
    remaps.push([/\bVARCHAR\(MAX\)/gi, 'CLOB'])
    remaps.push([/\bDATETIME2?\b/gi, 'TIMESTAMP'])
    remaps.push([/\bBIT\b/gi, 'SMALLINT'])
    remaps.push([/\bMONEY\b/gi, 'DECIMAL(19,4)'])
    remaps.push([/\bSMALLMONEY\b/gi, 'DECIMAL(10,4)'])
    remaps.push([/\bTINYINT\b/gi, 'SMALLINT'])
    remaps.push([/\bIMAGE\b/gi, 'BLOB'])
    remaps.push([/\bNTEXT\b/gi, 'CLOB'])
    remaps.push([/\bTEXT\b/gi, 'CLOB'])
    remaps.push([/\bUNIQUEIDENTIFIER\b/gi, target === 'postgresql' ? 'UUID' : 'VARCHAR(36)'])
    remaps.push([/\bIDENTITY\s*\(\s*\d+\s*,\s*\d+\s*\)/gi, 'GENERATED ALWAYS AS IDENTITY'])
  }

  // SQL Server → Oracle
  if (source === 'sqlserver' && target === 'oracle') {
    remaps.push([/\bVARCHAR\((\d+)\)/gi, 'VARCHAR2($1)'])
    remaps.push([/\bNVARCHAR\((\d+)\)/gi, 'NVARCHAR2($1)'])
    remaps.push([/\bDATETIME2?\b/gi, 'TIMESTAMP'])
    remaps.push([/\bBIT\b/gi, 'NUMBER(1)'])
    remaps.push([/\bINTEGER\b/gi, 'NUMBER(10)'])
    remaps.push([/\bBIGINT\b/gi, 'NUMBER(19)'])
    remaps.push([/\bSMALLINT\b/gi, 'NUMBER(5)'])
  }

  // SQL Server → MySQL
  if (source === 'sqlserver' && target === 'mysql') {
    remaps.push([/\bNVARCHAR\((\d+)\)/gi, 'VARCHAR($1)'])
    remaps.push([/\bDATETIME2\b/gi, 'DATETIME(6)'])
    remaps.push([/\bMONEY\b/gi, 'DECIMAL(19,4)'])
    remaps.push([/\bUNIQUEIDENTIFIER\b/gi, 'CHAR(36)'])
  }

  // DB2 → SQL Server
  if (source === 'db2' && target === 'sqlserver') {
    remaps.push([/\bCLOB\b/gi, 'VARCHAR(MAX)'])
    remaps.push([/\bBLOB\b/gi, 'VARBINARY(MAX)'])
    remaps.push([/\bTIMESTAMP\b/gi, 'DATETIME2'])
    remaps.push([/\bGENERATED\s+ALWAYS\s+AS\s+IDENTITY/gi, 'IDENTITY(1,1)'])
  }

  return remaps
}

/**
 * Get function remapping rules for a dialect conversion.
 * Returns array of [regex, replacement] pairs.
 */
function getFunctionRemaps(source, target) {
  const remaps = []

  if (source === 'sqlserver') {
    if (target === 'db2' || target === 'postgresql') {
      remaps.push([/\bGETDATE\s*\(\s*\)/gi, 'CURRENT_TIMESTAMP'])
      remaps.push([/\bISNULL\s*\(/gi, 'COALESCE('])
      remaps.push([/\bLEN\s*\(/gi, target === 'db2' ? 'LENGTH(' : 'LENGTH('])
      remaps.push([/\bCHARINDEX\s*\(/gi, target === 'db2' ? 'LOCATE(' : 'POSITION('])
    }
    if (target === 'oracle') {
      remaps.push([/\bGETDATE\s*\(\s*\)/gi, 'SYSDATE'])
      remaps.push([/\bISNULL\s*\(/gi, 'NVL('])
    }
    if (target === 'mysql') {
      remaps.push([/\bGETDATE\s*\(\s*\)/gi, 'NOW()'])
      remaps.push([/\bISNULL\s*\(/gi, 'IFNULL('])
    }
  }

  if (source === 'db2') {
    if (target === 'sqlserver') {
      remaps.push([/\bCURRENT_TIMESTAMP\b/gi, 'GETDATE()'])
      remaps.push([/\bCURRENT\s+TIMESTAMP\b/gi, 'GETDATE()'])
    }
  }

  return remaps
}

// ---------------------------------------------------------------------------
// Target node executors
// ---------------------------------------------------------------------------

/**
 * Execute the SQL Executor target node.
 * Collects SQL from upstream nodes, resolves connection, calls backend.
 */
async function executeTargetSqlExecutor(node, upstreamData, onProgress, signal) {
  const config = node.data?.config || {}
  const connSnapshot = node.data?.connectionSnapshot || null

  // Validate connection
  if (!connSnapshot || !connSnapshot.driverId) {
    throw new Error('SQL Executor: No database connection configured. Open the Connection Manager and assign a connection.')
  }

  // Collect SQL from all upstream inputs + pick up create_database from source node
  const allSql = []
  let upstreamCreateDb = false
  let upstreamDbName = ''
  for (const data of upstreamData) {
    if (data?.sql) {
      allSql.push(data.sql)
    }
    if (data?.create_database) upstreamCreateDb = true
    if (data?.database_name) upstreamDbName = data.database_name
  }

  if (allSql.length === 0) {
    throw new Error('SQL Executor: No SQL received from upstream nodes. Connect a DDL or DML Source node.')
  }

  let combinedSql = allSql.join('\n\n')

  // Strip stored procedures — the backend splits SQL on ';' which breaks
  // proc bodies that contain internal semicolons. Strip them regardless of
  // which transform nodes are in the pipeline.
  const procRegex = /(?:--[^\n]*\n)*CREATE\s+(?:OR\s+(?:ALTER|REPLACE)\s+)?PROCEDURE\b[\s\S]*?\nEND\s*;\s*/gi
  const procMatches = combinedSql.match(procRegex)
  if (procMatches && procMatches.length > 0) {
    combinedSql = combinedSql.replace(procRegex, '')
    onProgress(5, `Stripped ${procMatches.length} stored procedures (incompatible with statement-level execution)`)
  }

  // Resolve connection config — must be before create_database check
  const connFields = node.data?.config || {}
  const driverKey = resolveDriverKey(connSnapshot.driverId)

  // Build pre/post SQL
  // create_database/database_name can come from this node's config OR from upstream (DDL Source)
  const shouldCreateDb = config.create_database || upstreamCreateDb
  const targetDbName = config.database_name || upstreamDbName
  const parts = []
  if (shouldCreateDb && targetDbName) {
    const dbName = targetDbName.replace(/[^a-zA-Z0-9_-]/g, '_')
    if (driverKey === 'db2') {
      // DB2: create a schema (namespace for tables) + set it as current
      parts.push(`CREATE SCHEMA "${dbName}" AUTHORIZATION db2inst1`)
      parts.push(`SET CURRENT SCHEMA = '${dbName}'`)
    } else if (driverKey === 'sqlserver') {
      parts.push(`IF DB_ID('${dbName}') IS NULL CREATE DATABASE [${dbName}]`)
    } else {
      parts.push(`CREATE DATABASE IF NOT EXISTS ${dbName}`)
    }
  }

  // Auto-prepend DROP TABLE for each unique CREATE TABLE in the upstream SQL.
  // DDL pipelines are re-runnable: tables get dropped and recreated cleanly.
  // Any DROP errors (table doesn't exist yet) are skipped via onError='continue'.
  // Use a Set to avoid duplicate DROPs when the same table name appears multiple times.
  const tableNameSet = new Set()
  const createRegex = /CREATE\s+TABLE\s+("?[\w.]+"?)\s*\(/gi
  let m
  while ((m = createRegex.exec(combinedSql)) !== null) {
    tableNameSet.add(m[1])
  }
  if (tableNameSet.size > 0) {
    const dropStatements = [...tableNameSet].map(t => `DROP TABLE ${t}`).join(';\n')
    parts.push(dropStatements)
  }

  if (config.pre_sql?.trim()) {
    parts.push(config.pre_sql.trim())
  }
  parts.push(combinedSql)
  if (config.post_sql?.trim()) {
    parts.push(config.post_sql.trim())
  }

  const fullSql = parts.join('\n;\n')

  onProgress(10, `Connecting to ${connSnapshot.name || driverKey}...`)

  if (signal?.aborted) throw new Error('Execution cancelled')

  // Call backend — credentials come from config (spread from connectionFields) or snapshot
  const payload = {
    driver: driverKey,
    host: connSnapshot.host || connFields.host || 'localhost',
    port: connSnapshot.port || connFields.port || '',
    database: connSnapshot.database || connFields.database || '',
    username: connFields.username || connSnapshot.username || '',
    password: connFields.password || connSnapshot.password || '',
    schema: connSnapshot.schema || connFields.schema || '',
    sql: fullSql,
    delimiter: config.delimiter || ';',
    onError: config.on_error || 'continue',
    useTransaction: config.use_transaction || false,
  }

  onProgress(20, `Executing SQL against ${driverKey}...`)

  const result = await executeSql(payload)

  onProgress(100, result.success
    ? `Done: ${result.succeeded}/${result.totalStatements} statements succeeded (${result.durationMs}ms)`
    : `Completed with errors: ${result.failed} failed, ${result.succeeded} succeeded`)

  return {
    ...result,
    type: 'sql_execution',
    label: node.data?.label || 'SQL Executor',
  }
}

// ---------------------------------------------------------------------------
// EtlPipelineExecutor class
// ---------------------------------------------------------------------------

export class EtlPipelineExecutor {
  constructor(nodes, edges) {
    this.nodes = nodes || []
    this.edges = edges || []
    this.listeners = new Map()
    this.aborted = false
    this.abortController = new AbortController()
    this.nodeResults = new Map() // nodeId → result
  }

  // --- Event system ---

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event).push(callback)
    return this
  }

  emit(event, data) {
    const callbacks = this.listeners.get(event) || []
    for (const cb of callbacks) {
      try { cb(data) } catch { /* swallow listener errors */ }
    }
  }

  get signal() {
    return this.abortController.signal
  }

  abort() {
    this.aborted = true
    this.abortController.abort()
    this.emit(EtlEvents.PIPELINE_ERROR, { error: 'Pipeline cancelled by user' })
  }

  // --- Topological sort (Kahn's algorithm) ---

  getOrderedNodes() {
    const nodeMap = new Map()
    for (const node of this.nodes) {
      nodeMap.set(node.id, { node, inDegree: 0, children: [] })
    }

    // Build adjacency
    for (const edge of this.edges) {
      const parent = nodeMap.get(edge.source)
      const child = nodeMap.get(edge.target)
      if (parent && child) {
        parent.children.push(edge.target)
        child.inDegree++
      }
    }

    // BFS from nodes with inDegree 0
    const queue = []
    for (const [, entry] of nodeMap) {
      if (entry.inDegree === 0) {
        queue.push(entry.node)
      }
    }

    const ordered = []
    while (queue.length > 0) {
      const current = queue.shift()
      ordered.push(current)
      const entry = nodeMap.get(current.id)
      for (const childId of entry.children) {
        const childEntry = nodeMap.get(childId)
        childEntry.inDegree--
        if (childEntry.inDegree === 0) {
          queue.push(childEntry.node)
        }
      }
    }

    return ordered
  }

  /**
   * Get upstream data for a node by following incoming edges.
   */
  getUpstreamData(nodeId) {
    const upstream = []
    for (const edge of this.edges) {
      if (edge.target === nodeId) {
        const result = this.nodeResults.get(edge.source)
        if (result) {
          upstream.push(result)
        }
      }
    }
    return upstream
  }

  // --- Main execution loop ---

  async execute() {
    const startTime = Date.now()

    this.emit(EtlEvents.PIPELINE_START, {
      totalNodes: this.nodes.length,
      timestamp: new Date().toISOString(),
    })

    try {
      const orderedNodes = this.getOrderedNodes()

      if (orderedNodes.length === 0) {
        throw new Error('Pipeline is empty — add nodes to the canvas first.')
      }

      let nodesExecuted = 0
      let nodesSkipped = 0
      let nodesFailed = 0

      for (const node of orderedNodes) {
        if (this.aborted) break

        const nodeType = node.data?.type || node.data?.nodeType || node.type || ''
        const nodeLabel = node.data?.label || nodeType

        // Skip disabled nodes
        if (node.data?.enabled === false) {
          nodesSkipped++
          this.emit(EtlEvents.NODE_SKIPPED, { nodeId: node.id, nodeType, label: nodeLabel })
          continue
        }

        // Progress callback for this node
        const onProgress = (progress, message) => {
          this.emit(EtlEvents.NODE_PROGRESS, {
            nodeId: node.id,
            nodeType,
            label: nodeLabel,
            progress,
            message,
          })
        }

        this.emit(EtlEvents.NODE_START, { nodeId: node.id, nodeType, label: nodeLabel })

        try {
          let result

          // --- Sources ---
          if (nodeType === 'etl-source-ddl' || nodeType === 'etl-source-dml') {
            onProgress(0, `Loading SQL from ${nodeLabel}...`)
            result = executeSourceNode(node)
            onProgress(100, `${result.statementCount} statement(s) loaded from ${nodeLabel}`)

          // --- SQL Transforms ---
          } else if (nodeType === 'etl-transform-sql-dedup') {
            const upstreamData = this.getUpstreamData(node.id)
            result = executeSqlDedup(node, upstreamData, onProgress)
          } else if (nodeType === 'etl-transform-sql-types') {
            const upstreamData = this.getUpstreamData(node.id)
            result = executeSqlTypeNormalizer(node, upstreamData, onProgress)
          } else if (nodeType === 'etl-transform-sql-dialect') {
            const upstreamData = this.getUpstreamData(node.id)
            result = executeSqlDialectConverter(node, upstreamData, onProgress)

          // --- Targets ---
          } else if (nodeType === 'etl-target-sql-executor' || nodeType === 'etl-target-database') {
            const upstreamData = this.getUpstreamData(node.id)
            result = await executeTargetSqlExecutor(node, upstreamData, onProgress, this.signal)
          } else {
            // Unsupported node type — skip with warning
            onProgress(100, `Node type "${nodeType}" does not have an executor yet. Skipped.`)
            result = { type: 'unsupported', nodeType, skipped: true }
          }

          this.nodeResults.set(node.id, result)
          nodesExecuted++

          this.emit(EtlEvents.NODE_COMPLETE, {
            nodeId: node.id,
            nodeType,
            label: nodeLabel,
            result,
          })
        } catch (err) {
          nodesFailed++
          this.emit(EtlEvents.NODE_ERROR, {
            nodeId: node.id,
            nodeType,
            label: nodeLabel,
            error: err.message,
          })
          // Stop pipeline on node failure
          break
        }
      }

      const durationMs = Date.now() - startTime

      if (this.aborted) {
        this.emit(EtlEvents.PIPELINE_ERROR, {
          error: 'Pipeline cancelled by user',
          durationMs,
          nodesExecuted,
          nodesSkipped,
          nodesFailed,
        })
      } else if (nodesFailed > 0) {
        this.emit(EtlEvents.PIPELINE_ERROR, {
          error: `Pipeline completed with ${nodesFailed} failed node(s)`,
          durationMs,
          nodesExecuted,
          nodesSkipped,
          nodesFailed,
        })
      } else {
        this.emit(EtlEvents.PIPELINE_COMPLETE, {
          durationMs,
          nodesExecuted,
          nodesSkipped,
          nodesFailed,
        })
      }
    } catch (err) {
      this.emit(EtlEvents.PIPELINE_ERROR, {
        error: err.message,
        durationMs: Date.now() - startTime,
      })
    }
  }
}
